import { useEffect, useState } from 'react';

import { getHomeAnnouncementData } from '@/services/announcementService';

import { useAuthContext } from '@/contexts/AuthContext';
import { useFirebaseContext } from '@/contexts/FirebaseContext';

import { showErrorToast } from '@/components/common/ToastContainer';

import sessionStorageService from '../../../utility/sessionStorageService';
import { USER_ANNOUNCEMENT_SEEN_MAP } from '../constants';

function useAnnouncements(communityId) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collectionDocRef, setCollectionDocRef] = useState(null);

  const { user } = useAuthContext();
  const userId = user?._id;

  const { firestoreUtils } = useFirebaseContext();

  const fetchUserSeenAnnouncements = async (docRef) => {
    const data = await firestoreUtils?.fetchDoc(docRef);
    const announcementsData = data?.announcements || null;

    // Store in session storage to reduce number of reads on subsequent homepage lands
    if (announcementsData) {
      sessionStorageService.setItem(
        USER_ANNOUNCEMENT_SEEN_MAP,
        announcementsData
      );
    }
    return announcementsData;
  };

  const setHaveSeenAnnouncement = async (announcementId) => {
    if (!collectionDocRef) return;
    // Update firestore
    await firestoreUtils?.updateDoc(
      collectionDocRef,
      { announcements: firestoreUtils?.addToDocArray(announcementId) },
      { merge: true }
    );

    // Update session storage
    const sessionStorageData =
      sessionStorageService.getItem(USER_ANNOUNCEMENT_SEEN_MAP) || '';
    sessionStorageService.setItem(
      USER_ANNOUNCEMENT_SEEN_MAP,
      `${
        sessionStorageData ? `${sessionStorageData},` : ''
      }${announcementId}`
    );

    setAnnouncements((prev) =>
      prev.filter((announcement) => announcement._id !== announcementId)
    );
  };

  const fetchAnnouncementData = async () => {
    setLoading(true);
    const { data, error } = await getHomeAnnouncementData(communityId);

    if (error) {
      showErrorToast(error);
      setLoading(false);
      return;
    }
    const allAnnouncements = data?.data;

    // if no announcements just end the function
    if (!allAnnouncements.length) {
      setLoading(false);
      return;
    }

    // creating a reference to user's document in firestore
    const docRef = user
      ? firestoreUtils?.createDocRef(USER_ANNOUNCEMENT_SEEN_MAP, userId)
      : null;
    setCollectionDocRef(docRef);

    const sessionStorageSeenAnnouncements = sessionStorageService.getItem(
      USER_ANNOUNCEMENT_SEEN_MAP
    );

    // Only fetch from firestore if data doesn't exist in session storage
    const seenAnnouncements = sessionStorageSeenAnnouncements
      ? sessionStorageSeenAnnouncements.split(',')
      : await fetchUserSeenAnnouncements(docRef);

    const notSeenAnnouncements = allAnnouncements.filter(
      (announcement) => !seenAnnouncements?.includes(announcement?._id)
    );

    setAnnouncements(notSeenAnnouncements);
    setLoading(false);
  };

  useEffect(() => {
    if (communityId) {
      fetchAnnouncementData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  return { announcements, loading, setHaveSeenAnnouncement };
}

export default useAnnouncements;
